<template>
  <section class="legal-page" :dir="direction">
    <header class="legal-header">
      <button type="button" :aria-label="$t('legalUi.back')" @click="$emit('close')"><i class="el-icon-arrow-left" /></button>
      <div>
        <small>STARLIVE LEGAL</small>
        <strong>{{ document ? document.title : fallbackTitle }}</strong>
      </div>
      <span />
    </header>

    <div class="legal-scroll">
      <div v-if="loading" class="legal-state"><i class="el-icon-loading" /><strong>{{ $t("legalUi.loading") }}</strong></div>
      <div v-else-if="error || !document" class="legal-state">
        <i class="el-icon-document-delete" />
        <strong>{{ $t("legalUi.loadFailed") }}</strong>
        <p>{{ $t("legalUi.unavailable") }}</p>
        <button type="button" @click="loadDocument">{{ $t("common.retry") }}</button>
      </div>
      <article v-else>
        <div class="legal-hero">
          <span><i class="el-icon-document-checked" /></span>
          <small>{{ $t("legalUi.legalNotice") }}</small>
          <h1>{{ document.title }}</h1>
          <p>{{ document.summary }}</p>
          <div>
            <em>{{ $t("legalUi.version", { version: document.version }) }}</em>
            <em v-if="document.effective_time">{{ $t("legalUi.effective", { date: formatDate(document.effective_time) }) }}</em>
          </div>
        </div>
        <div class="legal-content" v-html="document.content_html" />
        <footer>{{ $t("legalUi.updated", { date: formatDate(document.update_time) }) }}</footer>
      </article>
    </div>
  </section>
</template>

<script>
import { getLegalDocument } from "../api/legal"
import { localeState } from "../i18n"

export default {
  name: "LegalDocumentViewer",
  props: {
    type: { type: String, required: true }
  },
  data() {
    return { loading: false, error: false, document: null }
  },
  computed: {
    fallbackTitle() {
      const titleKeys = { terms: "settingsUi.terms", privacy: "settingsUi.privacy", community: "settingsUi.community" }
      return this.$t(titleKeys[this.type] || titleKeys.terms)
    },
    direction() {
      return String(localeState.language).toLowerCase().startsWith("ar") ? "rtl" : "ltr"
    },
    localeLanguage() {
      return localeState.language
    }
  },
  watch: {
    type: "loadDocument",
    localeLanguage: "loadDocument"
  },
  created() {
    this.loadDocument()
  },
  methods: {
    async loadDocument() {
      this.loading = true
      this.error = false
      try {
        const res = await getLegalDocument(this.type)
        this.document = res.data.document
      } catch (error) {
        this.document = null
        this.error = true
      } finally {
        this.loading = false
      }
    },
    formatDate(value) {
      if (!value) return ""
      const normalized = String(value).includes("T") ? value : `${String(value).replace(" ", "T")}Z`
      return this.$d(new Date(normalized), "short")
    }
  }
}
</script>

<style scoped>
.legal-page{position:fixed;z-index:4100;top:0;bottom:0;left:50%;width:min(100vw,430px);transform:translateX(-50%);display:flex;flex-direction:column;overflow:hidden;color:#f8f5ef;background:radial-gradient(circle at 88% 0,rgba(255,70,94,.23),transparent 30%),linear-gradient(180deg,#121318,#0c0d10 42%)}button{font:inherit}.legal-header{height:calc(66px + env(safe-area-inset-top));padding:env(safe-area-inset-top) 16px 0;display:grid;grid-template-columns:40px 1fr 40px;align-items:center;border-bottom:1px solid rgba(255,255,255,.08);background:rgba(13,14,17,.78);backdrop-filter:blur(20px)}.legal-header button{width:36px;height:36px;border:1px solid #363840;border-radius:50%;background:#1b1c21;color:#fff}.legal-header div{min-width:0;text-align:center}.legal-header small,.legal-header strong{display:block}.legal-header small{color:#ff596d;font-size:8px;letter-spacing:2.2px}.legal-header strong{margin-top:3px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;font-size:14px}.legal-scroll{flex:1;overflow-y:auto;scrollbar-width:none}.legal-scroll::-webkit-scrollbar{display:none}.legal-state{min-height:70vh;display:grid;place-content:center;justify-items:center;padding:24px;text-align:center;color:#90929a}.legal-state>i{font-size:42px;color:#ff5368}.legal-state strong{margin-top:14px;color:#fff}.legal-state p{max-width:260px;font-size:12px;line-height:1.7}.legal-state button{margin-top:8px;padding:10px 18px;border:0;border-radius:20px;background:#ff4059;color:#fff}.legal-hero{margin:18px 16px 0;padding:25px 20px 20px;border:1px solid rgba(255,255,255,.1);border-radius:22px;background:linear-gradient(145deg,rgba(255,255,255,.08),rgba(255,255,255,.025));box-shadow:0 24px 50px rgba(0,0,0,.2)}.legal-hero>span{width:44px;height:44px;display:grid;place-items:center;border-radius:15px;background:linear-gradient(135deg,#ff4059,#ff8b51);font-size:20px;box-shadow:0 10px 24px rgba(255,64,89,.24)}.legal-hero>small{display:block;margin-top:18px;color:#ff7c8d;font-size:9px;letter-spacing:1.2px}.legal-hero h1{margin:7px 0 9px;font-size:25px;line-height:1.3}.legal-hero p{margin:0;color:#a8aab1;font-size:12px;line-height:1.75}.legal-hero div{display:flex;flex-wrap:wrap;gap:7px;margin-top:16px}.legal-hero em{padding:6px 9px;border-radius:10px;background:#22242a;color:#bfc0c5;font-size:9px;font-style:normal}.legal-content{padding:24px 20px 34px;color:#cacbd0;font-size:14px;line-height:1.9;overflow-wrap:anywhere}.legal-content ::v-deep h2,.legal-content ::v-deep h3,.legal-content ::v-deep h4{color:#fff;line-height:1.45}.legal-content ::v-deep h2{margin:16px 0 20px;font-size:21px}.legal-content ::v-deep h3{position:relative;margin:28px 0 10px;padding-inline-start:13px;font-size:16px}.legal-content ::v-deep h3:before{content:"";position:absolute;inset-inline-start:0;top:4px;width:3px;height:17px;border-radius:2px;background:#ff4b62}.legal-content ::v-deep p{margin:0 0 14px}.legal-content ::v-deep ul,.legal-content ::v-deep ol{padding-inline-start:22px}.legal-content ::v-deep li{margin-bottom:8px}.legal-content ::v-deep a{color:#ff7284}.legal-content ::v-deep img{display:block;max-width:100%;height:auto;margin:16px auto;border-radius:12px}.legal-content ::v-deep blockquote{margin:16px 0;padding:12px 14px;border-inline-start:3px solid #ff4b62;background:#191a1f;color:#a7a9b0}footer{padding:0 20px calc(30px + env(safe-area-inset-bottom));color:#6f7179;font-size:10px;text-align:center}[dir="rtl"] .legal-header button i{transform:rotate(180deg)}
</style>
